import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminCreditEntry, type AdminOrgBilling } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { StatTile } from '@/components/shell/StatTile';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet } from '@/components/ui/sheet';
import { CREDIT_PAGE_SIZE, formatCount, formatDateTime, formatUsd } from '@/lib/format';
import { resolveLoadStatus } from '@/lib/loadState';
import { orgSectionPath } from '@/lib/orgNav';
import { humanizeKey, type StatusTone } from '@/lib/runTelemetry';

const COLUMNS = [
  { label: 'Type' },
  { label: 'Description' },
  { label: 'Amount', className: 'text-right' },
  { label: 'When', className: 'text-right' },
];

const ENTRY_TONES: Record<string, StatusTone> = { grant: 'success', spend: 'neutral' };
const ENTRY_LABELS: Record<string, string> = { grant: 'Granted', spend: 'Used' };

function spendKindLabel(entry: AdminCreditEntry): string | undefined {
  if (entry.source === 'llm-usage') return 'AI building';
  if (entry.source === 'integration-usage') return 'Integrations';
  return undefined;
}

/** A raw request UUID is not a description; say what kind of spend it was instead. */
function entryDescription(entry: AdminCreditEntry): string {
  const kind = spendKindLabel(entry);
  if (kind) return kind;
  const reason = entry.reason?.trim() || '';
  const sourceId = entry.sourceId?.trim();
  if (sourceId && reason.includes(sourceId)) {
    return reason.replaceAll(sourceId, '').replace(/\s+/g, ' ').trim() || 'Usage';
  }
  return reason || '—';
}

export function CreditHistoryPanel({
  orgId,
  billing,
  onClose,
}: {
  orgId: string;
  billing?: AdminOrgBilling;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<AdminCreditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [entriesError, setEntriesError] = useState<string | undefined>();

  const loadEntries = useCallback(
    async (nextOffset: number) => {
      setLoading(true);
      try {
        const result = await adminClient.listOrgCreditEntries(orgId, { limit: CREDIT_PAGE_SIZE, offset: nextOffset });
        setEntries(result.items);
        setTotal(result.total);
        setOffset(result.offset);
        setEntriesError(undefined);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load credit history';
        setEntriesError(message);
        toast.error(message);
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    },
    [orgId],
  );

  useEffect(() => {
    void loadEntries(0);
  }, [loadEntries]);

  const entriesStatus = resolveLoadStatus({ loaded, error: entriesError, hasData: entries.length > 0 });
  const cents = (value?: number) => (billing ? formatUsd((value ?? 0) / 100) : '—');

  return (
    <Sheet
      open
      onClose={onClose}
      title="Credit history"
      description="Credit pays for AI building and integrations first. The card is only charged once it runs out."
      className="sm:max-w-2xl"
    >
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <StatTile label="Remaining" value={cents(billing?.creditBalanceCents)} />
        <StatTile label="Granted" value={cents(billing?.creditGrantedCents)} />
        <StatTile label="Used" value={cents(billing?.creditSpentCents)} />
      </div>

      <DataTable
        toolbar={
          <p className="text-xs text-muted-foreground">
            {entriesStatus === 'ready' ? `${formatCount(total)} entr${total === 1 ? 'y' : 'ies'}` : '\u00a0'}
          </p>
        }
        footer={
          <TablePager
            total={total}
            limit={CREDIT_PAGE_SIZE}
            offset={offset}
            disabled={loading}
            onOffsetChange={(next) => void loadEntries(next)}
          />
        }
      >
        <DataTableHead columns={COLUMNS} />
        {entriesStatus === 'loading' || (loading && entries.length === 0) ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading credit history
          </DataTableMessage>
        ) : entriesStatus === 'error' ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={entriesError} onRetry={() => void loadEntries(offset)} />
        ) : entries.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>No credit granted or spent yet</DataTableMessage>
        ) : (
          <tbody className={loading ? 'opacity-60' : undefined}>
            {entries.map((entry) => (
              <DataTableRow key={entry.id}>
                <DataTableCell>
                  <StatusPill tone={ENTRY_TONES[entry.type] ?? 'neutral'}>{ENTRY_LABELS[entry.type] ?? humanizeKey(entry.type)}</StatusPill>
                </DataTableCell>
                <DataTableCell>
                  {entry.projectId && entry.projectTitle ? (
                    <div className="min-w-0">
                      <Link
                        to={`${orgSectionPath(orgId, 'projects')}/${encodeURIComponent(entry.projectId)}`}
                        className="font-medium text-foreground hover:underline"
                      >
                        {entry.projectTitle}
                      </Link>
                      {spendKindLabel(entry) ? (
                        <p className="text-xs text-muted-foreground">{spendKindLabel(entry)}</p>
                      ) : null}
                    </div>
                  ) : (
                    <span className="break-words">{entryDescription(entry)}</span>
                  )}
                </DataTableCell>
                <DataTableCell className="whitespace-nowrap text-right tabular-nums">
                  {entry.type === 'spend' ? '−' : entry.type === 'grant' ? '+' : ''}
                  {formatUsd(Math.abs(entry.amountCents) / 100)}
                </DataTableCell>
                <DataTableCell className="whitespace-nowrap text-right text-muted-foreground">
                  {formatDateTime(entry.createdAt)}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>
    </Sheet>
  );
}

export function GrantCreditPanel({
  orgId,
  onClose,
  onGranted,
}: {
  orgId: string;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [amountUsd, setAmountUsd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amount = Number(amountUsd);
    const why = reason.trim();
    if (!Number.isFinite(amount) || amount <= 0 || !why) {
      toast.error('Enter an amount and a reason');
      return;
    }
    setBusy(true);
    try {
      const next = await adminClient.grantOrgCredit(orgId, {
        amountUsd: amount,
        reason: why,
        idempotencyKey:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `grant-${Date.now()}`,
      });
      toast.success(
        next.alreadyApplied ? 'That grant was already applied' : `Granted ${formatUsd((next.grantedCents ?? 0) / 100)}`,
      );
      onGranted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to grant credit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Grant credit"
      description="Credit is spent before any card charge. The reason shows in the credit history."
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="grant-credit-form" disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Grant credit
          </Button>
        </>
      }
    >
      <form
        id="grant-credit-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div>
          <Label htmlFor="grant-amount">Amount (USD)</Label>
          <Input
            id="grant-amount"
            className="mt-2"
            type="number"
            min="0.01"
            step="0.01"
            inputMode="decimal"
            placeholder="100.00"
            value={amountUsd}
            onChange={(event) => setAmountUsd(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="grant-reason">Reason</Label>
          <Input
            id="grant-reason"
            className="mt-2"
            placeholder="Pilot credit"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </form>
    </Sheet>
  );
}
